import { Vue, Component, Ref, Emit, Provide } from 'vue-property-decorator'
import BillingModalEntityComponent from '@/modules/billing-module/shared/components/modal-entity/billing-modal-entity.vue'
import BillingModalEntity from '@/modules/billing-module/shared/components/modal-entity/billing-modal-entity'
import BillingModalInvoiceReceiptCardComponent from '@/modules/billing-module/components/modal/invoice-receipt/components/card/billing-modal-invoice-receipt-card.vue'
import BillingModalInvoiceReceiptFormComponent from '@/modules/billing-module/components/modal/invoice-receipt/components/form/billing-modal-invoice-receipt-form.vue'
import BillingModalInvoiceReceiptActionsGridComponent from '@/modules/billing-module/components/modal/invoice-receipt/components/actions/grid/billing-modal-invoice-receipt-actions-grid.vue'
import BillingModalInvoiceReceiptActionsGrid from '@/modules/billing-module/components/modal/invoice-receipt/components/actions/grid/billing-modal-invoice-receipt-actions-grid'
import BillingModalInvoiceReceiptActionsModalComponent from '@/modules/billing-module/components/modal/invoice-receipt/components/actions/modal/billing-modal-invoice-receipt-actions-modal.vue'
import {
    BillingModalInvoiceReceiptParams,
    BillingModalInvoiceReceiptState,
    initialBillingModalInvoiceReceiptState
} from '@/modules/billing-module/components/modal/invoice-receipt/billing-modal-invoice-receipt-model'
import { InvoiceReceiptStatus } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.dto'
import { billingInvoiceReceiptService } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.service'
import { cloneDeep } from 'lodash'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { GridUtils } from '@/shared/util/grid-utils'
import { CommonFilterModel } from '@/api/resources/common/shared/models/common.models'
import { BillingConfigInvoiceConceptsGridDatasource } from '@/modules/billing-module/shared/config/invoice-concepts-grid/billing-config-invoice-concepts-grid.datasource'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { BillingModalEntityParams } from '@/modules/billing-module/shared/components/modal-entity/billing-modal-entity-model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        BillingModalEntityComponent,
        BillingModalInvoiceReceiptCardComponent,
        BillingModalInvoiceReceiptFormComponent,
        BillingModalInvoiceReceiptActionsGridComponent,
        BillingModalInvoiceReceiptActionsModalComponent
    }
})
export default class BillingModalInvoiceReceipt extends Vue {
    @Ref() readonly invoiceReceiptModal!: BillingModalEntity<AppliedBillableConceptResponse>
    @Ref() readonly gridActions!: BillingModalInvoiceReceiptActionsGrid

    @Provide() externalValidator = this.$validator

    @Emit()
    refresh(): void {
        // emits an event
    }

    state!: BillingModalInvoiceReceiptState
    constructor() {
        super()
        this.state = initialBillingModalInvoiceReceiptState()
    }

    /// COMPUTED ///

    private get _invoiceReceiptGenerated(): boolean {
        return this.state.invoiceReceipt?.status.code === InvoiceReceiptStatus.GENERATED
    }
    private get _invoiceReceiptVerified(): boolean {
        return this.state.invoiceReceipt?.status.code === InvoiceReceiptStatus.VERIFIED
    }

    private get _modalTitle(): string {
        return this.state.invoiceReceipt
            ? this.$tc('accounting.action.updateInvoiceReceipt')
            : this.$tc('accounting.action.generateInvoiceReceipt')
    }
    private static get _gridUiComponent(): string {
        return 'uicomponent.billing.grid.invoiceReceiptModal'
    }

    /// selection
    private get _selectionDatasource(): BillingConfigInvoiceConceptsGridDatasource {
        const filterModel: CommonFilterModel = {}
        if (this.state.invoice) {
            filterModel['deliveryNote.invoice.id'] = GridUtils.getNumberFilterModel('equals', this.state.invoice.id)
        }
        filterModel['invoiceReceipt'] = GridUtils.getNumberFilterModel('equals', null)
        return new BillingConfigInvoiceConceptsGridDatasource(filterModel)
    }
    private get _selectionTitle(): string {
        return this.state.addToMode
            ? this.$tc('accounting.invoiceReceiptModal.addConceptsTitle')
            : this.$tc('accounting.invoiceReceiptModal.conceptSelectionTitle')
    }
    private get _selectionGridTitle(): string {
        return `${this.$tc('accounting.field.invoice')} ${this.state.invoice?.label ?? '-'}`
    }

    /// crud
    private get _crudDatasource(): BillingConfigInvoiceConceptsGridDatasource {
        const filterModel: CommonFilterModel = {}
        if (this.state.invoiceReceipt) {
            filterModel['invoiceReceipt.id'] = GridUtils.getNumberFilterModel('equals', this.state.invoiceReceipt.id)
        }
        return new BillingConfigInvoiceConceptsGridDatasource(filterModel)
    }
    private get _crudGridKey(): number {
        return this.state.invoiceReceipt?.appliedBillableConceptCount ?? 0
    }
    private get _crudGridTitle(): string {
        return `${this.$tc('accounting.invoiceReceiptModal.invoiceReceiptConcepts')} (${this.state.conceptsTotalCount})`
    }
    private get _crudEmptyStateTitle(): string {
        return this.$tc('accounting.invoicePaymentModal.invoiceReceiptWithoutConcepts')
    }

    get modalEntityParams(): BillingModalEntityParams<AppliedBillableConceptResponse> {
        return {
            modalTitle: this._modalTitle,
            selectionMode: this.state.selectionMode,
            selection: {
                datasource: this._selectionDatasource,
                addToMode: this.state.addToMode,
                title: this._selectionTitle,
                gridTitle: this._selectionGridTitle,
                gridUiComponent: BillingModalInvoiceReceipt._gridUiComponent
            },
            crud: {
                datasource: this._crudDatasource,
                gridKey: `${this._crudGridKey}`,
                visibleGrid: true,
                gridTitle: this._crudGridTitle,
                gridUiComponent: BillingModalInvoiceReceipt._gridUiComponent,
                getContextMenuItems: this.getCrudContextMenuItems,
                emptyStateTitle: this._crudEmptyStateTitle
            }
        }
    }

    get visibleForm(): boolean {
        return this._invoiceReceiptVerified || this._invoiceReceiptGenerated
    }

    /// MODAL ///

    show(params: BillingModalInvoiceReceiptParams): void {
        this.state = initialBillingModalInvoiceReceiptState()

        const { invoice, invoiceReceipt } = params
        if (invoice) {
            this.state.invoice = cloneDeep(invoice)
        }
        if (invoiceReceipt) {
            this.state.invoiceReceipt = cloneDeep(invoiceReceipt)
            this.state.selectionMode = false
        }

        this.invoiceReceiptModal.show()
    }
    hide(): void {
        this.invoiceReceiptModal.hide()
    }

    /// SERVICES CALLS ///

    private async _reloadInvoiceReceipt(invoiceReceiptId: number): Promise<boolean> {
        const response =
            await PromiseUtils.toCallSecure(
                billingInvoiceReceiptService.get(store.state.labId, invoiceReceiptId))
        if (response !== null) {
            this.state.invoiceReceipt = response
        }
        return response !== null
    }

    /// EVENT HANDLERS ///

    onConceptsAdded(): void {
        this.state.selectionMode = true
        this.state.addToMode = true
    }
    onHideAddToMode(): void {
        this.state.selectionMode = false
        this.state.addToMode = false
    }

    onConceptsTotalCountChanged(totalCount: number): void {
        this.state.conceptsTotalCount = totalCount
    }
    onConceptsSelected(appliedBillableConcepts: AppliedBillableConceptResponse[]): void {
        this.state.appliedBillableConcepts = appliedBillableConcepts
    }

    async onRefresh(invoiceReceiptId: StrictNullable<number>): Promise<void> {
        if (invoiceReceiptId) {
            if (await this._reloadInvoiceReceipt(invoiceReceiptId)) {
                this.state.selectionMode = false
            }
        } else {
            this.hide()
        }
        this.refresh() // emits an event
    }

    getCrudContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return this.gridActions?.getContextMenuItems(params) ?? []
    }
}
